import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Alert, Button, Card, Empty, Grid, Segmented, Spin, Typography } from 'antd'
import { ArrowRightOutlined, BarChartOutlined, ClockCircleOutlined, ReloadOutlined, WalletOutlined } from '@ant-design/icons'
import { Column } from '@ant-design/charts'
import dayjs from 'dayjs'
import { API_URL } from '../../config/runtimeConfig'
import './customerCommissionDashboard.css'

const client = axios.create({ baseURL:API_URL||undefined, timeout:30000 })
const money = value => Number(value||0).toLocaleString('th-TH',{minimumFractionDigits:2,maximumFractionDigits:2})
const thaiMonths = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
const monthLabel = key => {
  const [year,month] = String(key).split('-').map(Number)
  return `${thaiMonths[month-1]||''} ${String(year+543).slice(-2)}`
}
const thaiDate = value => {
  const date=dayjs(value)
  return date.isValid()?`${date.date()} ${thaiMonths[date.month()]} ${date.year()+543}`:'—'
}

export default function CustomerCommissionDashboard({ token, onOpenEstimate, onNewJob }) {
  const screens=Grid.useBreakpoint()
  const isMobile=!screens.md
  const [data,setData]=useState(null)
  const [loading,setLoading]=useState(true)
  const [error,setError]=useState('')
  const [months,setMonths]=useState(6)

  const load=useCallback(async()=>{
    setLoading(true)
    setError('')
    try {
      const response=await client.get('/api/customer-estimates/commission-dashboard',
        {headers:{Authorization:`Bearer ${token}`}})
      setData(response.data.data)
    } catch(requestError) {
      setError(requestError.response?.data?.message||'โหลดข้อมูลค่าคอมมิชชั่นไม่สำเร็จ')
    } finally { setLoading(false) }
  },[token])
  useEffect(()=>{load()},[load])

  const chartRows=useMemo(()=>{
    if(!data) return []
    const estimateMap=new Map((data.estimated_by_month||[]).map(row=>[row.month,Number(row.amount)]))
    const paidMap=new Map((data.paid_by_month||[]).map(row=>[row.month,Number(row.amount)]))
    return Array.from({length:months},(_,index)=>{
      const key=dayjs().startOf('month').subtract(months-1-index,'month').format('YYYY-MM')
      return [{period:key,series:'ยอดตามใบประเมิน',amount:estimateMap.get(key)||0},
        {period:key,series:'รับแล้ว',amount:paidMap.get(key)||0}]
    }).flat()
  },[data,months])
  const hasChartValues=chartRows.some(row=>row.amount>0)
  const currentMonth=dayjs().format('YYYY-MM')
  const thisMonthPaid=data?.paid_by_month?.find(row=>row.month===currentMonth)?.amount||'0.00'
  const chartConfig={
    data:chartRows,xField:'period',yField:'amount',colorField:'series',
    height:isMobile?255:308,transform:[{type:'dodgeX'}],
    scale:{color:{range:['#a9caff','#127bba']}},
    axis:{x:{labelFormatter:monthLabel},y:{labelFormatter:value=>Number(value).toLocaleString('th-TH')}},
    tooltip:{items:[{channel:'y',valueFormatter:value=>`${money(value)} บาท`}]},
    legend:false
  }

  return <div className="customer-commission-dashboard">
    <section className="customer-commission-hero">
      <div className="customer-commission-hero-copy">
        <span className="customer-commission-eyebrow">COMMISSION DASHBOARD</span>
        <h1>ภาพรวมค่าคอมมิชชั่น</h1>
        <p>ติดตามยอดจากใบประเมินและยอดที่บันทึกจ่ายจริงของงานคุณ</p>
        <div className="customer-commission-hero-total"><span>รับแล้วทั้งหมด</span><strong>{money(data?.paid_total)} <small>บาท</small></strong></div>
        <span className="customer-commission-hero-note">ยอดนี้นับจากรายการที่บันทึกจ่ายแล้ว</span>
      </div>
    </section>

    {error&&<Alert type="error" showIcon message={error} action={<Button size="small" onClick={load}>ลองอีกครั้ง</Button>}/>}
    <div className="customer-commission-toolbar"><span>ข้อมูลค่าคอมมิชชั่นของคุณ</span><Button icon={<ReloadOutlined/>} loading={loading} onClick={load}>รีเฟรช</Button></div>
    <Spin spinning={loading}>
      <div className="customer-commission-kpis">
        <div className="customer-commission-kpi"><span className="customer-commission-kpi-icon blue"><WalletOutlined/></span><span>รับแล้วเดือนนี้</span><strong>{money(thisMonthPaid)} <small>บาท</small></strong><em>ตามวันที่บันทึกจ่าย</em></div>
        <div className="customer-commission-kpi"><span className="customer-commission-kpi-icon teal"><ClockCircleOutlined/></span><span>อนุมัติแล้ว รอจ่าย</span><strong>{money(data?.approved_unpaid_total)} <small>บาท</small></strong><em>ยังไม่ใช่รายได้รับจริง</em></div>
        <div className="customer-commission-kpi"><span className="customer-commission-kpi-icon violet"><BarChartOutlined/></span><span>ค่าคอมมิชชั่นตามใบประเมิน</span><strong>{money(data?.estimated_total)} <small>บาท</small></strong><em>จาก {Number(data?.estimated_jobs||0).toLocaleString('th-TH')} งานที่ยังดำเนินการ</em></div>
      </div>

      <Card className="customer-commission-chart-card" bordered={false}>
        <div className="customer-commission-card-heading"><div><span className="customer-commission-section-kicker">แนวโน้มรายเดือน</span><h2>ค่าคอมมิชชั่นตามช่วงเวลา</h2><p>ยอดประเมินจัดตามเดือนที่เปิดงาน · ยอดรับจริงจัดตามเดือนที่บันทึกจ่าย</p></div>
          <Segmented value={months} onChange={setMonths} options={[{label:'6 เดือน',value:6},{label:'12 เดือน',value:12}]}/></div>
        <div className="customer-commission-legend"><span><i className="estimate"/>ยอดตามใบประเมิน</span><span><i className="paid"/>รับแล้ว</span></div>
        {hasChartValues?<div className="customer-commission-chart-scroll"><div style={{minWidth:months===12&&isMobile?660:undefined}}><Column {...chartConfig}/></div></div>
          :<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ยังไม่มีข้อมูลค่าคอมมิชชั่นในช่วงที่เลือก" className="customer-commission-empty"/>}
      </Card>

      <Card className="customer-commission-recent-card" bordered={false}>
        <div className="customer-commission-card-heading"><div><span className="customer-commission-section-kicker">รายการล่าสุด</span><h2>ประวัติการรับค่าคอมมิชชั่น</h2><p>แสดงรายการที่บันทึกจ่ายแล้วล่าสุด</p></div></div>
        {data?.recent_paid?.length?<div className="customer-commission-recent-list">{data.recent_paid.map(row=><button type="button" className="customer-commission-recent-row" key={row.id} onClick={()=>onOpenEstimate(row.estimate_id)}>
          <span className="customer-commission-recent-symbol">฿</span><span className="customer-commission-recent-description"><strong>{row.title}</strong><small>{row.estimate_number} · {thaiDate(row.paid_at)}</small></span><span className="customer-commission-recent-amount">+{money(row.paid_amount)} บาท</span><ArrowRightOutlined className="customer-commission-recent-arrow"/>
        </button>)}</div>:<div className="customer-commission-no-payments"><WalletOutlined/><strong>ยังไม่มีรายการจ่ายค่าคอมมิชชั่น</strong><span>เมื่อมีการบันทึกจ่าย รายการจะปรากฏที่นี่</span>{Number(data?.estimated_jobs||0)===0&&<Button type="primary" onClick={onNewJob}>ส่งงานใหม่</Button>}</div>}
      </Card>
      <Typography.Text className="customer-commission-disclaimer">ยอดตามใบประเมินเป็นเพียงยอดคาดการณ์ การรับค่าคอมมิชชั่นจะนับเมื่อพนักงานบันทึกจ่ายในระบบแล้ว</Typography.Text>
    </Spin>
  </div>
}
